const ORDERS_PREVIEW_SRC = '/orders-preview.png'

// ── Icons ─────────────────────────────────────────────────────────────────────

const IconTable = ({ color = 'currentColor' }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <rect x="2" y="6" width="18" height="3" rx="1.5" stroke={color} strokeWidth="1.5"/>
    <path d="M6 9v7M16 9v7" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M4 16h14" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

const IconDelivery = ({ color = 'currentColor' }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path d="M2 9h12v7H2z" rx="1" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M14 11l3-3h3v5h-3l-3 1V11z" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    <circle cx="6" cy="17.5" r="1.5" stroke={color} strokeWidth="1.5"/>
    <circle cx="16" cy="17.5" r="1.5" stroke={color} strokeWidth="1.5"/>
  </svg>
)

const IconBag = ({ color = 'currentColor' }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <rect x="3.5" y="8" width="15" height="11" rx="2" stroke={color} strokeWidth="1.5"/>
    <path d="M8 8V7a3 3 0 0 1 6 0v1" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 13h6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

const orderTypes = [
  {
    IconComponent: IconTable,
    name: 'Na mesa',
    desc: 'Clientes pedem direto da mesa escaneando o QR code. O pedido cai no painel automaticamente.',
    features: ['QR code por mesa', 'Pedido vai direto para a cozinha', 'Controle de conta por mesa'],
    featured: false,
  },
  {
    IconComponent: IconDelivery,
    name: 'Delivery',
    desc: 'Receba pedidos de entrega com dados do cliente, endereço e taxa configurável. Sem comissão de plataforma.',
    features: ['Sem taxa por pedido', 'Dados de entrega do cliente', 'Status de preparo em tempo real'],
    featured: true,
  },
  {
    IconComponent: IconBag,
    name: 'Retirada',
    desc: 'O cliente pede pelo site e retira no balcão no horário que escolher. Prático para movimentos rápidos.',
    features: ['Agendamento de retirada', 'Notificação quando pronto', 'Sem fila no caixa'],
    featured: false,
  },
]

export default function OrderTypesSection() {
  return (
    <>
      <section className="bg-neutral-50 px-5 py-16 sm:px-8 sm:py-20 lg:px-16 lg:py-24 scroll-mt-24" id="pedidos">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-[11px] font-bold uppercase tracking-[2px] text-neutral-400 mb-3">
            Gestão de pedidos
          </div>
          <h2 className="text-[30px] sm:text-[38px] lg:text-[50px] font-black tracking-tight leading-[1.1] mb-4">
            Mesa, delivery ou retirada.<br />Tudo no mesmo lugar.
          </h2>
          <p className="text-base lg:text-[17px] text-neutral-500 leading-[1.7] max-w-[500px]">
            Um painel unificado para gerenciar todos os seus pedidos em tempo real, com status customizáveis e impressão automática.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10 lg:mt-16">
            {orderTypes.map((type) => (
              <div
                key={type.name}
                className={`rounded-[18px] p-7 border ${type.featured ? 'text-white' : 'bg-white border-neutral-200'}`}
                style={type.featured ? { background: '#141410', borderColor: '#141410' } : {}}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-5 ${type.featured ? 'bg-white/10' : 'bg-neutral-100'}`}>
                  <type.IconComponent color={type.featured ? 'white' : '#141410'} />
                </div>
                <div className={`text-[18px] font-extrabold mb-2 tracking-tight ${type.featured ? 'text-white' : ''}`}>
                  {type.name}
                </div>
                <p className={`text-[14px] leading-[1.65] mb-4 ${type.featured ? 'text-white/55' : 'text-neutral-500'}`}>
                  {type.desc}
                </p>
                <div className="flex flex-col gap-2">
                  {type.features.map((feat) => (
                    <div
                      key={feat}
                      className={`text-[13px] flex items-start gap-2 leading-[1.4] ${type.featured ? 'text-white/60' : 'text-neutral-500'}`}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="flex-shrink-0 mt-0.5">
                        <path d="M3 7l2.5 2.5L11 4.5" stroke={type.featured ? 'rgba(255,255,255,0.45)' : '#141410'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {feat}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-8 xl:gap-12 mt-10 lg:mt-16 lg:items-start">
            <div className="flex-1 min-w-0 max-w-[520px] lg:max-w-[38%] xl:max-w-[400px] lg:flex-shrink-0">
              <div className="text-[11px] font-bold uppercase tracking-[2px] text-neutral-400 mb-3">
                Painel em tempo real
              </div>
              <h3 className="text-[24px] sm:text-[28px] lg:text-[32px] font-black tracking-tight leading-[1.15] mb-3 sm:mb-4">
                Cada pedido no seu radar.
              </h3>
              <p className="text-[14px] sm:text-[15px] text-neutral-500 leading-[1.7] max-w-[500px] mb-5">
                Acompanhe todos os pedidos por status — pendente, preparando, pronto, entregando. Filtre por tipo de atendimento e nunca perca um pedido.
              </p>
              <ul className="space-y-2.5">
                {['Atualização em tempo real', 'Filtros por mesa, delivery e retirada', 'Status de pagamento visível'].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-neutral-600">
                    <span className="w-5 h-5 rounded-full bg-black text-white flex items-center justify-center text-[11px] font-bold shrink-0">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1 min-w-0 w-full flex justify-center lg:justify-end">
              <div className="w-full max-w-[640px] sm:max-w-[720px] mx-auto lg:mx-0 lg:max-w-none rounded-2xl overflow-hidden border border-neutral-200 bg-white shadow-[0_28px_80px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04]">
                <img
                  src={ORDERS_PREVIEW_SRC}
                  alt="Painel de pedidos — busca, filtros por status e lista de pedidos com mesa, delivery e pagamento"
                  className="w-full h-auto block select-none"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
